import React, { useState, useEffect, useCallback } from "react";
import { Globe, Plus, Trash2, ChevronDown, CalendarDays, ListChecks, FileSignature, MapPin, GitBranch, Users, LineChart, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["Agency", "Tour Operator", "Airline", "Other"];
const TABS = [
  { id: "prospects", label: "Prospects", icon: Users },
  { id: "agencies", label: "Agencies & TOs", icon: Users },
  { id: "pipeline", label: "Pipeline", icon: GitBranch },
  { id: "markets", label: "Markets", icon: MapPin },
  { id: "meetings", label: "Meetings", icon: CalendarDays },
  { id: "actions", label: "Actions", icon: ListChecks },
  { id: "agreements", label: "Agreements", icon: FileSignature },
  { id: "performance", label: "Performance", icon: LineChart },
];
const stageColor = (s, stages) => {
  const i = stages.indexOf(s);
  const pct = stages.length > 1 ? i / (stages.length - 1) : 0;
  return pct >= 0.85 ? "bg-[#DCFCE7] text-[#047857]" : pct >= 0.5 ? "bg-[#E0F2FE] text-[#0369A1]" : "bg-slate-100 text-slate-600";
};

export default function GsaWorldwide() {
  const { activeOrgId, activeMembership } = useAuth();
  const admin = ["Owner", "Administrator"].includes(activeMembership?.role);
  const [enabled, setEnabled] = useState(null);
  const [stages, setStages] = useState([]);
  const [tab, setTab] = useState("prospects");
  const [prospects, setProspects] = useState([]);
  const [overview, setOverview] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Agency", market: "", stage: "Identified" });

  const check = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/gsa/enabled`);
    setEnabled(data.enabled); setStages(data.stages || []);
  }, [activeOrgId]);
  const loadAll = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const [p, o] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/gsa/prospects`),
        api.get(`/organizations/${activeOrgId}/gsa/overview`),
      ]);
      setProspects(p.data.prospects || []); setOverview(o.data);
    } catch { /* noop */ }
  }, [activeOrgId]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => { if (enabled) loadAll(); }, [enabled, loadAll]);

  const createProspect = async () => {
    if (!form.name.trim()) return toast.error("Nom requis");
    try { await api.post(`/organizations/${activeOrgId}/gsa/prospects`, form); setForm({ name: "", type: "Agency", market: "", stage: "Identified" }); toast.success("Prospect ajouté"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const setStage = async (id, stage) => {
    setProspects((ps) => ps.map((p) => p.id === id ? { ...p, stage } : p));
    try { await api.patch(`/organizations/${activeOrgId}/gsa/prospects/${id}`, { stage }); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const del = async (id) => { try { await api.delete(`/organizations/${activeOrgId}/gsa/prospects/${id}`); toast.success("Supprimé"); loadAll(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); } };
  const addItem = async (id, kind, title, date) => {
    if (!title.trim()) return;
    try { await api.post(`/organizations/${activeOrgId}/gsa/prospects/${id}/items`, { kind, title, date }); toast.success("Ajouté"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  if (enabled === null) return <div className="py-20 text-center text-sm text-slate-400" data-testid="gsa-loading">Chargement…</div>;
  if (!enabled) return (
    <div className="rounded-2xl border border-slate-200 bg-white py-20 text-center" data-testid="gsa-disabled">
      <Lock size={36} className="mx-auto mb-3 text-slate-300" />
      <h2 className="font-heading text-lg font-bold text-[#17201A]">GSA Worldwide n'est pas activé</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">Ce module optionnel de représentation commerciale internationale doit être activé par Lodge & Co pour votre organisation.</p>
    </div>
  );

  const filtered = tab === "agencies" ? prospects.filter((p) => ["Agency", "Tour Operator"].includes(p.type)) : prospects;

  return (
    <div className="space-y-6" data-testid="gsa-page">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]"><Globe size={18} className="text-[#5C9873]" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">GSA Worldwide</h1>
          <p className="text-sm text-[#64748B]">Représentation commerciale internationale — pipeline de prospects & agences.</p>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`gsa-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold ${tab === t.id ? "border-[#1E3D2B] text-[#1E3D2B]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* Prospects & Agencies */}
      {(tab === "prospects" || tab === "agencies") && (
        <div className="space-y-4" data-testid="gsa-prospects">
          {admin && tab === "prospects" && (
            <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex-1 min-w-[160px]"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Nom</label><Input data-testid="gsa-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Agence / TO" /></div>
              <div className="min-w-[140px]"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="gsa-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="min-w-[130px]"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Marché</label><Input data-testid="gsa-market" value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} placeholder="Germany" /></div>
              <button data-testid="gsa-add-prospect" onClick={createProspect} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]"><Plus size={15} /> Ajouter</button>
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-sm text-slate-400" data-testid="gsa-empty">Aucun prospect.</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {filtered.map((p) => (
                <div key={p.id} data-testid={`gsa-prospect-${p.id}`} className="border-b border-slate-100 last:border-0">
                  <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <div className="flex-1 min-w-[160px]"><p className="text-sm font-semibold text-[#17201A]">{p.name}</p><p className="text-xs text-slate-400">{p.type}{p.market ? ` · ${p.market}` : ""}</p></div>
                    {admin ? (
                      <Select value={p.stage} onValueChange={(v) => setStage(p.id, v)}><SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`gsa-stage-${p.id}`}><SelectValue /></SelectTrigger><SelectContent>{stages.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent></Select>
                    ) : <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageColor(p.stage, stages)}`}>{p.stage}</span>}
                    <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="text-xs font-semibold text-[#3E7256] hover:underline"><ChevronDown size={14} className={`inline ${expanded === p.id ? "rotate-180" : ""}`} /> détails</button>
                    {admin && <button data-testid={`gsa-del-${p.id}`} onClick={() => del(p.id)} className="text-slate-300 hover:text-rose-600"><Trash2 size={15} /></button>}
                  </div>
                  {expanded === p.id && (
                    <div className="grid grid-cols-1 gap-3 bg-[#FBFCFB] px-5 py-3 sm:grid-cols-3" data-testid={`gsa-detail-${p.id}`}>
                      {["meeting", "action", "agreement"].map((kind) => (
                        <ItemColumn key={kind} kind={kind} items={p[kind + "s"]} admin={admin} onAdd={(title, date) => addItem(p.id, kind, title, date)} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pipeline */}
      {tab === "pipeline" && overview && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7" data-testid="gsa-pipeline">
          {stages.map((s) => (
            <div key={s} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`gsa-stage-col-${s}`}>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{s}</p>
              <p className="font-heading text-xl font-bold text-[#1E3D2B]">{overview.by_stage[s] || 0}</p>
              <div className="mt-2 space-y-1">{prospects.filter((p) => p.stage === s).slice(0, 5).map((p) => <p key={p.id} className="truncate rounded bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">{p.name}</p>)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Markets */}
      {tab === "markets" && overview && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="gsa-markets">
          {Object.keys(overview.by_market).length === 0 ? <p className="text-sm text-slate-400">Aucun marché.</p> :
            Object.entries(overview.by_market).map(([m, n]) => (
              <div key={m} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-sm font-semibold text-[#17201A]">{m}</p><p className="font-heading text-2xl font-bold text-[#1E3D2B]">{n}</p><p className="text-xs text-slate-400">prospect{n > 1 ? "s" : ""}</p></div>
            ))}
        </div>
      )}

      {/* Meetings / Actions / Agreements */}
      {["meetings", "actions", "agreements"].includes(tab) && overview && (
        <AggList items={overview[tab]} tab={tab} />
      )}

      {/* Performance */}
      {tab === "performance" && overview && (
        <div className="space-y-4" data-testid="gsa-performance">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Stat label="Prospects" value={overview.performance.prospects_count} />
            <Stat label="Représentations actives" value={overview.performance.active_representations} />
            <Stat label="Accords" value={overview.performance.agreements_count} />
            <Stat label="Valeur des accords" value={new Intl.NumberFormat("fr-FR").format(overview.performance.agreements_value || 0)} />
            <Stat label="Conversion" value={overview.performance.conversion == null ? "—" : `${(overview.performance.conversion * 100).toFixed(0)}%`} />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="gsa-revenue-by-market">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Revenu généré par marché (Data Hub)</p>
            {Object.keys(overview.performance.revenue_by_market || {}).length === 0 ? (
              <p className="text-sm text-slate-400">Aucun revenu importé pour les marchés des prospects.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.performance.revenue_by_market).map(([m, v]) => (
                  <span key={m} className="rounded-lg bg-[#F1F7F3] px-3 py-1.5 text-sm text-[#2C5540]">{m} · <span className="font-mono font-semibold">{new Intl.NumberFormat("fr-FR").format(v)}</span></span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemColumn({ kind, items, admin, onAdd }) {
  const [title, setTitle] = useState(""); const [date, setDate] = useState("");
  const label = { meeting: "Meetings", action: "Actions", agreement: "Agreements" }[kind];
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="space-y-1">{(items || []).map((it) => <p key={it.id} className="rounded bg-white px-2 py-1 text-[11px] text-slate-600">{it.title}{it.date ? ` · ${it.date}` : ""}</p>)}</div>
      {admin && (
        <div className="mt-1.5 flex gap-1">
          <input data-testid={`gsa-item-${kind}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`+ ${kind}`} className="h-7 flex-1 rounded border border-slate-200 px-2 text-[11px] outline-none" />
          <button onClick={() => { onAdd(title, date); setTitle(""); }} className="rounded bg-[#3E7256] px-2 text-[11px] font-semibold text-white">+</button>
        </div>
      )}
    </div>
  );
}

function AggList({ items, tab }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`gsa-${tab}`}>
      {(!items || items.length === 0) ? <div className="py-12 text-center text-sm text-slate-400">Aucun élément.</div> :
        items.map((it, i) => (
          <div key={i} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 last:border-0">
            <div><p className="text-sm font-medium text-[#17201A]">{it.title || "—"}</p><p className="text-xs text-slate-400">{it.prospect}{it.date ? ` · ${it.date}` : ""}{it.status ? ` · ${it.status}` : ""}</p></div>
            {it.value ? <span className="font-mono text-sm text-[#1E3D2B]">{new Intl.NumberFormat("fr-FR").format(it.value)}</span> : null}
          </div>
        ))}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-1 font-heading text-2xl font-bold text-[#17201A]">{value}</p></div>
);
